import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
    Wallet,
    TrendingUp,
    TrendingDown,
    Lock,
    Network,
    Activity,
    ShieldCheck,
    CheckCircle2,
    Mail,
    AlertTriangle,
    AlertOctagon,
    XCircle,
    Coins,
    Receipt,
} from "lucide-react";

const STEPS = [
    { n: 1, t: "Connect Wallet", b: "Link your Web3 wallet (or open a testnet account) to the protocol.", icon: Wallet },
    { n: 2, t: "Choose Bull or Bear", b: "Select your price direction view on any supported Hyperliquid market.", icon: TrendingUp },
    { n: 3, t: "Lock Margin", b: "Deposit your margin (minimum 2% of notional). That is the maximum you can lose.", icon: Lock },
    { n: 4, t: "Smart Matching", b: "Protocol pairs you with someone holding the opposite view at the same terms.", icon: Network },
    { n: 5, t: "Trade Runs", b: "Position is marked-to-market in real time against the Hyperliquid oracle.", icon: Activity },
    { n: 6, t: "Auto-Close Safety", b: "If you burn 95% of your margin, the position is closed to protect you.", icon: ShieldCheck },
    { n: 7, t: "Settlement", b: "At expiry the protocol pays out cash. Winner receives the capped PnL, loser is capped at margin.", icon: CheckCircle2 },
];

const COMPARE_ROWS = [
    ["Loss Limit", "Unlimited", "Can exceed margin", "Always capped at margin"],
    ["Complexity", "High", "Very high", "Simple UP / DOWN"],
    ["Liquidation Risk", "Yes", "Yes", "None"],
    ["Funding Rates", "N/A", "Yes (hidden cost)", "None"],
    ["Leverage Traps", "Common", "Very common", "Impossible"],
    ["Control", "Exchange-dependent", "Exchange-dependent", "Smart contract"],
    ["Transparency", "Low", "Medium", "Full (on-chain)"],
    ["Counterparty", "Exchange / Broker", "Exchange", "Peer (matched)"],
];

export default function HowItWorks() {
    const { user } = useAuth();
    const ctaHref = user ? "/terminal" : "/auth?mode=register";

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)]">
            <div className="bg-grid-fine">
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-[#1F1F24]">
                    <div className="absolute inset-0 hero-bg opacity-90 pointer-events-none" />
                    <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20 relative">
                        <div className="inline-flex items-center gap-2 border border-[#1F1F24] bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-dot" />
                            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-400">Protocol Mechanics</span>
                        </div>
                        <h1 className="mt-8 text-4xl lg:text-6xl font-medium tracking-tighter leading-[0.95] max-w-4xl">
                            How <span className="text-cyan-400">BUBE</span> Works
                        </h1>
                        <p className="mt-6 text-zinc-400 text-lg max-w-2xl leading-relaxed">
                            A transparent, peer-to-peer trading protocol where disagreement creates opportunity — without the complexity.
                            No leverage traps. No liquidation cascades. No hidden rules.
                        </p>
                        <p className="mt-3 text-zinc-500 text-sm font-mono">Trade UP vs DOWN — loss capped from the time of entry to exit.</p>

                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link
                                to={ctaHref}
                                data-testid="hiw-cta-launch"
                                className="px-6 py-3 bg-cyan-400 text-black font-semibold tracking-tight text-sm rounded-sm hover:bg-cyan-300 transition-colors"
                            >
                                Trade Now →
                            </Link>
                            <a
                                href="#core-loop"
                                className="px-6 py-3 border border-[#1F1F24] hover:border-zinc-700 text-zinc-200 font-medium tracking-tight text-sm rounded-sm transition-colors"
                            >
                                Read the mechanics
                            </a>
                        </div>
                    </div>
                </section>

                {/* DUAL CARDS: BULL vs BEAR */}
                <section className="border-b border-[#1F1F24] py-16 px-6 lg:px-12">
                    <div className="max-w-[1280px] mx-auto grid md:grid-cols-2 gap-2">
                        <DirectionCard
                            kind="BULLISH"
                            title="Price Goes UP"
                            subtitle="Bet on rising markets with capped downside"
                            Icon={TrendingUp}
                            color="emerald"
                        />
                        <DirectionCard
                            kind="BEARISH"
                            title="Price Goes DOWN"
                            subtitle="Profit from falling markets with defined risk"
                            Icon={TrendingDown}
                            color="rose"
                        />
                    </div>
                </section>

                {/* THE CORE LOOP */}
                <section id="core-loop" className="border-b border-[#1F1F24] py-20 px-6 lg:px-12">
                    <div className="max-w-[1280px] mx-auto">
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">From disagreement to fair settlement</div>
                        <h2 className="mt-3 text-3xl lg:text-5xl font-medium tracking-tighter">A BUBE swap, step by step.</h2>
                        <p className="mt-4 text-zinc-400 max-w-2xl">Two people disagree on price direction. Both lock a small amount — their maximum risk. Time passes. The real market price decides. One wins, one loses. Loss is capped at the locked margin. No guessing. No house. No exchange control.</p>

                        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {STEPS.map((s) => {
                                const Icon = s.icon;
                                return (
                                    <div key={s.n} className="bg-[#0A0A0C] border border-[#1F1F24] p-6 rounded-sm hover:border-zinc-700 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="font-mono text-[10px] tracking-[0.22em] text-cyan-400">STEP {String(s.n).padStart(2, "0")}</div>
                                            <Icon className="w-4 h-4 text-zinc-600" />
                                        </div>
                                        <h3 className="mt-4 text-lg font-medium tracking-tight">{s.t}</h3>
                                        <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{s.b}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* WHAT IS BUBE - 4 PILLARS */}
                <section className="border-b border-[#1F1F24] py-20 px-6 lg:px-12">
                    <div className="max-w-[1280px] mx-auto">
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">What is Verslan BUBE</div>
                        <h2 className="mt-3 text-3xl lg:text-5xl font-medium tracking-tighter max-w-3xl">Pick a side. Let it ride.</h2>

                        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-2">
                            {[
                                ["Bullish–Bearish Execution", "Two traders take opposite price views on any asset."],
                                ["Margin Lock", "Both parties lock their margin in a smart contract."],
                                ["Loss Always Capped", "Maximum loss = your locked margin. Nothing more."],
                                ["One Wins, One Loses", "Settlement is automatic based on actual price movement."],
                            ].map(([t, b], i) => (
                                <div key={t} className="bg-[#0A0A0C] border border-[#1F1F24] p-6 rounded-sm">
                                    <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500">0{i + 1}</div>
                                    <h3 className="mt-3 text-lg font-medium tracking-tight">{t}</h3>
                                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{b}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FEE STRUCTURE */}
                <section id="fees" className="border-b border-[#1F1F24] py-20 px-6 lg:px-12">
                    <div className="max-w-[1280px] mx-auto">
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Fee structure · transparent</div>
                        <h2 className="mt-3 text-3xl lg:text-5xl font-medium tracking-tighter">All fees, listed.</h2>
                        <p className="mt-4 text-zinc-400 max-w-2xl">A single entry fee charged the moment your swap is matched. Short and medium tenors (15m → 24h) have <span className="text-emerald-300">no settlement fee</span>. Long tenors (15d / 1mo / 1yr) add a <span className="text-cyan-300">0.10% settlement fee</span> at expiry. Range across all durations: <span className="text-zinc-200 font-mono">0.025% → 0.50%</span>.</p>

                        <div className="mt-12 grid lg:grid-cols-3 gap-2">
                            <div className="lg:col-span-2 bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-[#1F1F24] flex items-center gap-2">
                                    <Receipt className="w-4 h-4 text-cyan-400" />
                                    <div className="text-sm font-medium tracking-tight">Entry-fee tiers · % of notional · charged once at match</div>
                                </div>
                                <table className="w-full font-mono text-sm" data-testid="fee-table">
                                    <thead>
                                        <tr className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 border-b border-[#1F1F24]">
                                            <th className="text-left px-5 py-3 font-normal">Duration</th>
                                            <th className="text-right px-5 py-3 font-normal">Entry fee</th>
                                            <th className="text-right px-5 py-3 font-normal">On $10,000 notional</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            ["15 minutes", "0.025%", "$2.50"],
                                            ["30 minutes", "0.05%", "$5.00"],
                                            ["1 hour", "0.10%", "$10.00"],
                                            ["3 hours", "0.15%", "$15.00"],
                                            ["6 hours", "0.15%", "$15.00"],
                                            ["12 hours", "0.15%", "$15.00"],
                                            ["24 hours", "0.15%", "$15.00"],
                                            ["15 days", "0.20%", "$20.00"],
                                            ["1 month", "0.35%", "$35.00"],
                                            ["1 year", "0.50%", "$50.00"],
                                        ].map(([d, f, ex]) => (
                                            <tr key={d} className="border-t border-[#1F1F24]/60">
                                                <td className="px-5 py-2.5 text-zinc-200">{d}</td>
                                                <td className="px-5 py-2.5 text-right text-cyan-400">{f}</td>
                                                <td className="px-5 py-2.5 text-right text-zinc-500">{ex}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="px-5 py-3 border-t border-[#1F1F24] text-[10px] font-mono text-zinc-500 leading-relaxed">
                                    Note · 15d, 1mo, 1yr tenors also incur a <span className="text-cyan-300">0.10% settlement fee</span> on notional at expiry, paid by the loser side. All other tenors have no settlement fee.
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-5">
                                    <div className="flex items-center gap-2">
                                        <Coins className="w-4 h-4 text-cyan-400" />
                                        <div className="text-sm font-medium tracking-tight">Settlement fee</div>
                                    </div>
                                    <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
                                        <span className="text-zinc-200">0.10% on notional</span> applies <span className="text-cyan-300">only for long-duration swaps</span> (15 days, 1 month, 1 year) — paid by the loser at expiry. Short and medium swaps (15 min → 24 h) have <span className="text-emerald-300">no settlement fee</span>, just the entry trading fee.
                                    </p>
                                </div>
                                <div className="bg-[#0A0A0C] border border-rose-500/20 rounded-sm p-5">
                                    <div className="flex items-center gap-2">
                                        <AlertOctagon className="w-4 h-4 text-rose-400" />
                                        <div className="text-sm font-medium tracking-tight">Liquidation fee</div>
                                    </div>
                                    <p className="mt-2 text-xs text-zinc-400 leading-relaxed">If your position is auto-closed at 95% margin depletion, the protocol sweeps whatever remains of your margin (max 5%) as the liquidation fee — capped at <span className="text-rose-300">2% of notional</span>. Anything above that cap is returned to you. No other liquidation surcharge.</p>
                                </div>
                                <div className="bg-[#0A0A0C] border border-amber-500/20 rounded-sm p-5">
                                    <div className="flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                                        <div className="text-sm font-medium tracking-tight">Early-exit penalty</div>
                                    </div>
                                    <p className="mt-2 text-xs text-zinc-400 leading-relaxed">Voluntarily closing a position before expiry: <span className="text-amber-300">2% of your margin</span> to the counterparty + <span className="text-amber-300">1% of your margin</span> to Verslan. Current MtM PnL is settled normally.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* TRAFFIC LIGHT */}
                <section className="border-b border-[#1F1F24] py-20 px-6 lg:px-12">
                    <div className="max-w-[1280px] mx-auto">
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">No panic trades · No negative balance · No forced liquidation</div>
                        <h2 className="mt-3 text-3xl lg:text-5xl font-medium tracking-tighter max-w-3xl">You can't lose more than you lock.</h2>
                        <p className="mt-4 text-zinc-400 max-w-2xl">Built-in traffic-light system protects your capital automatically.</p>

                        <div className="mt-12 grid md:grid-cols-3 gap-2">
                            <TrafficLight pct="80%" label="Warning" Icon={AlertTriangle} color="text-amber-300" border="border-amber-500/30" detail="You've used 80% of your margin." />
                            <TrafficLight pct="90%" label="Strong Warning" Icon={AlertOctagon} color="text-orange-400" border="border-orange-500/30" detail="You've used 90% of your margin — close soon." />
                            <TrafficLight pct="95%" label="Auto Close" Icon={XCircle} color="text-rose-400" border="border-rose-500/30" detail="Position automatically closed to protect you." />
                        </div>

                        <div className="mt-12 grid md:grid-cols-3 gap-2 text-sm text-zinc-300">
                            <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 rounded-sm">Maximum loss = your locked margin.</div>
                            <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 rounded-sm">Clear warnings give you time to decide.</div>
                            <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 rounded-sm">You control when to close before 95%.</div>
                        </div>
                    </div>
                </section>

                {/* NOTIONAL vs MARGIN */}
                <section className="border-b border-[#1F1F24] py-20 px-6 lg:px-12">
                    <div className="max-w-[1280px] mx-auto">
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Core concept</div>
                        <h2 className="mt-3 text-3xl lg:text-5xl font-medium tracking-tighter max-w-3xl">Notional vs Margin.</h2>
                        <p className="mt-4 text-zinc-400 max-w-2xl">The two numbers that make BUBE both safe and simple.</p>

                        <div className="mt-12 grid md:grid-cols-2 gap-2">
                            <div className="bg-[#0A0A0C] border border-[#1F1F24] p-8 rounded-sm">
                                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Notional</div>
                                <div className="mt-1 text-2xl font-medium tracking-tight">Not real money</div>
                                <ul className="mt-6 space-y-3 text-sm text-zinc-300">
                                    <li className="flex gap-3"><span className="text-cyan-400 font-mono">→</span>Notional is the reference number.</li>
                                    <li className="flex gap-3"><span className="text-cyan-400 font-mono">→</span>It is used only to calculate profit and loss percentage.</li>
                                </ul>
                                <div className="mt-8 font-mono text-3xl tracking-tighter text-zinc-100">$10,000</div>
                                <div className="text-xs text-zinc-500 mt-1 font-mono">Example notional — reference only</div>
                            </div>

                            <div className="bg-[#0A0A0C] border border-cyan-500/40 p-8 rounded-sm">
                                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">Margin</div>
                                <div className="mt-1 text-2xl font-medium tracking-tight">Real money at risk</div>
                                <ul className="mt-6 space-y-3 text-sm text-zinc-300">
                                    <li className="flex gap-3"><span className="text-cyan-400 font-mono">→</span>Margin is the only real money in the trade.</li>
                                    <li className="flex gap-3"><span className="text-cyan-400 font-mono">→</span>This is your maximum possible loss. Period.</li>
                                </ul>
                                <div className="mt-8 font-mono text-3xl tracking-tighter text-cyan-400">$200</div>
                                <div className="text-xs text-zinc-500 mt-1 font-mono">Min 2% of notional — capped loss</div>
                            </div>
                        </div>

                        <div className="mt-8 bg-[#0A0A0C] border border-[#1F1F24] p-8 rounded-sm">
                            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Think of it like this</div>
                            <div className="mt-3 grid md:grid-cols-3 gap-4 text-sm text-zinc-300">
                                <div>Two friends make a bet on the price of bitcoin.</div>
                                <div>The bet is <span className="font-mono text-zinc-100">worth $10,000</span> — that is the notional.</div>
                                <div>Each puts <span className="font-mono text-cyan-400">$200</span> on the table. That is the margin and the maximum they can lose.</div>
                            </div>
                            <div className="mt-6 grid grid-cols-2 gap-2">
                                <div className="bg-[#050505] border border-[#1F1F24] p-5 rounded-sm">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Notional</div>
                                    <div className="font-mono text-3xl tracking-tighter mt-1">$10,000</div>
                                    <div className="text-xs text-zinc-500 mt-1">= reference (just for math)</div>
                                </div>
                                <div className="bg-[#050505] border border-cyan-500/40 p-5 rounded-sm">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">Margin</div>
                                    <div className="font-mono text-3xl tracking-tighter mt-1 text-cyan-400">$200</div>
                                    <div className="text-xs text-zinc-500 mt-1">= real money at risk</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* COMPARISON */}
                <section className="border-b border-[#1F1F24] py-20 px-6 lg:px-12">
                    <div className="max-w-[1280px] mx-auto">
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Why BUBE is different</div>
                        <h2 className="mt-3 text-3xl lg:text-5xl font-medium tracking-tighter">Simple. Transparent. Fair.</h2>
                        <p className="mt-4 text-zinc-400 max-w-2xl">Compare BUBE against traditional trading and perpetuals across the metrics that matter.</p>

                        <div className="mt-12 overflow-x-auto bg-[#0A0A0C] border border-[#1F1F24] rounded-sm">
                            <table className="w-full font-mono text-sm" data-testid="comparison-table">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                                        <th className="text-left px-5 py-4 font-normal">Feature</th>
                                        <th className="text-left px-5 py-4 font-normal">Traditional</th>
                                        <th className="text-left px-5 py-4 font-normal">Perpetuals / Options</th>
                                        <th className="text-left px-5 py-4 font-normal text-cyan-400">Verslan BUBE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {COMPARE_ROWS.map(([f, tr, pp, b]) => (
                                        <tr key={f} className="border-t border-[#1F1F24]">
                                            <td className="px-5 py-3 text-zinc-300">{f}</td>
                                            <td className="px-5 py-3 text-zinc-500">{tr}</td>
                                            <td className="px-5 py-3 text-zinc-500">{pp}</td>
                                            <td className="px-5 py-3 text-cyan-400">{b}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <p className="mt-6 text-zinc-500 text-sm">Even if price moves a lot, your loss stops at the margin.</p>
                    </div>
                </section>

                {/* ZERO IMPACT */}
                <section className="border-b border-[#1F1F24] py-20 px-6 lg:px-12">
                    <div className="max-w-[1280px] mx-auto grid lg:grid-cols-2 gap-12 items-start">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Engineered for safety</div>
                            <h2 className="mt-3 text-3xl lg:text-5xl font-medium tracking-tighter">Zero impact on flash crashes &amp; whale attacks.</h2>
                            <p className="mt-4 text-zinc-400 leading-relaxed">A fair Bullish–Bearish execution system where two traders disagree, lock margin, and settle automatically. Because the contract is pre-funded and the loss is capped at margin, no amount of slippage or coordinated dump can drain you beyond what you locked.</p>
                        </div>
                        <div className="grid gap-2">
                            <Pill label="No leverage traps" />
                            <Pill label="No liquidation cascades" />
                            <Pill label="No hidden funding rates" />
                            <Pill label="No exchange custody risk" />
                            <Pill label="Loss capped from entry to exit" />
                        </div>
                    </div>
                </section>

                {/* CTA + CONTACT */}
                <section className="py-24 px-6 lg:px-12">
                    <div className="max-w-[1280px] mx-auto text-center">
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Ready to trade?</div>
                        <h2 className="mt-3 text-3xl lg:text-5xl font-medium tracking-tighter">Disagree on price. Agree on risk.</h2>
                        <p className="mt-6 text-zinc-400 max-w-xl mx-auto">Join traders worldwide using BUBE for transparent, fair, and capped-risk trading.</p>
                        <Link
                            to={ctaHref}
                            data-testid="hiw-cta-bottom"
                            className="inline-block mt-10 px-8 py-3 bg-cyan-400 text-black font-semibold tracking-tight rounded-sm hover:bg-cyan-300 transition-colors"
                        >
                            Start Trading Now →
                        </Link>

                        <div className="mt-16 max-w-md mx-auto text-left">
                            <a
                                href="mailto:contact@verslan.xyz"
                                data-testid="contact-email-link"
                                className="bg-[#0A0A0C] border border-[#1F1F24] hover:border-cyan-500/40 p-5 rounded-sm flex items-center gap-3 transition-colors"
                            >
                                <Mail className="w-5 h-5 text-cyan-400" />
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Contact</div>
                                    <div className="font-mono text-sm text-zinc-100">contact@verslan.xyz</div>
                                </div>
                            </a>
                        </div>

                        <p className="mt-12 text-zinc-600 text-xs max-w-2xl mx-auto leading-relaxed">
                            Verslan BUBE is a decentralized protocol. Trading involves risk. Make sure you understand the mechanics before participating.
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
}

function DirectionCard({ kind, title, subtitle, Icon, color }) {
    const isUp = color === "emerald";
    return (
        <div
            data-testid={`direction-card-${kind}`}
            className={`bg-[#0A0A0C] border ${isUp ? "border-emerald-500/30" : "border-rose-500/30"} p-8 rounded-sm relative overflow-hidden group hover:border-zinc-700 transition-colors`}
        >
            <div className={`absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-10 ${isUp ? "bg-emerald-500" : "bg-rose-500"} blur-3xl`} />
            <div className="flex items-center gap-3 relative">
                <Icon className={`w-6 h-6 ${isUp ? "text-emerald-400" : "text-rose-400"}`} />
                <span className={`text-[10px] font-mono uppercase tracking-[0.22em] ${isUp ? "text-emerald-400" : "text-rose-400"}`}>{kind}</span>
            </div>
            <h3 className="mt-5 text-3xl font-medium tracking-tighter relative">{title}</h3>
            <p className="mt-3 text-zinc-400 text-sm relative">{subtitle}</p>
        </div>
    );
}

function TrafficLight({ pct, label, Icon, color, border, detail }) {
    return (
        <div className={`bg-[#0A0A0C] border ${border} p-6 rounded-sm`}>
            <div className="flex items-center justify-between">
                <Icon className={`w-6 h-6 ${color}`} />
                <span className={`text-[10px] font-mono uppercase tracking-[0.22em] ${color}`}>{label}</span>
            </div>
            <div className="mt-4 font-mono text-5xl tracking-tighter text-zinc-100">{pct}</div>
            <div className="mt-3 text-sm text-zinc-400">{detail}</div>
        </div>
    );
}

function Pill({ label }) {
    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] px-5 py-4 rounded-sm flex items-center gap-3">
            <CheckCircle2 className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-zinc-200">{label}</span>
        </div>
    );
}
